import { useTranslator } from '@bluebeam/react-i18n';
import LoadingAnimation from 'components/LoadingAnimation';
import ConfirmModal from 'components/modals/ConfirmModal';
import SubmittalLogUploadModal from 'components/modals/SubmittalLogUploadModal';
import { BannerContext } from 'components/Routes/BannerContext';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCheckbox,
} from 'components/StdList';
import Immutable from 'immutable';
import SubmittalsSubHeader from 'pages/SubmittalsPage/SubmittalsSubheader';
import React, { CSSProperties, useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { AddSubmittal } from 'records/interactions/AddSubmittal';
import { EditSubmittal } from 'records/interactions/EditSubmittal';
import { UpdateMultipleSubmittals } from 'records/interactions/UpdateMultipleSubmittals';
import { ViewSubmittal } from 'records/interactions/ViewSubmittal';
import { ModalState } from 'records/ModalState';
import { NewSubmittal } from 'records/NewSubmittal';
import { Submittal, SubmittalListTypes } from 'records/Submittal';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { v4 as uuidv4 } from 'uuid';
import NoSubmittals from '../NoSubmittals';
import SubmittalDeepLink from '../SubmittalDeepLink';
import SubmittalListHeaderCell from '../SubmittalListHeaderCell';
import SubmittalLogRenderer from './SubmittalLogRenderer';
import SubmittalLogTabSubHeader from './SubmittalLogTabSubHeader';

const gridTemplateColumns = '48px 1fr 1fr 1fr 1.2fr 1fr 1fr 1fr';

interface RenderingProps {
  style: CSSProperties;
  index: number;
  data: Immutable.OrderedMap<string, Submittal>;
}

interface StyleProps {
  height: number;
  width: number;
}

interface Props extends WithAppStoreProps, RouteComponentProps {}

const SubmittalLogTab = (props: Props) => {
  const { height: bannerHeight } = useContext(BannerContext);
  const [loadingSubmittals, setLoadingSubmittals] = useState<boolean>(true);
  const [uploadingSubmittalLog, setUploadingSubmittalLog] =
    useState<boolean>(false);

  const { translate } = useTranslator(['submittalsPage', 'statusLabels']);

  useEffect(() => {
    const loadSubmittals = async (): Promise<void> => {
      await props.actions.getSubmittals(SubmittalListTypes.ALL);
      await props.actions.querySubmittals();
      setLoadingSubmittals(false);
    };

    loadSubmittals();

    return () => {
      const { appState, actions } = props;

      actions.setSelectedSubmittals(
        appState.submittals.keySeq().toSet().clear()
      );
    };
  }, []);

  const handleAddSubmittal = (): void => {
    const { actions, appState } = props;

    // TODO: Figure out proper default for these dates below.
    const interaction = new AddSubmittal({
      submittal: new NewSubmittal({
        id: uuidv4(),
        projectid: appState.selectedProject,
        duefromsub: 0,
        duefromreviewer: 0,
        requiredonsite: 0,
      }),
    });

    actions.createInteraction(interaction);
  };

  const handleUploadSubmittalLog = (): void => {
    const modalState = new ModalState({
      id: 'SubmittalLogUploadModal',
      portal: 'main',
      open: true,
      component: SubmittalLogUploadModal,
      props: {
        onSubmittalLogSelect: handleSubmittalLogSelect,
      },
    });

    props.actions.createModal(modalState);
  };

  const handleSubmittalLogSelect = async (files: FileList): Promise<void> => {
    const { actions } = props;

    try {
      setUploadingSubmittalLog(true);
      // only allowing one file upload per time for now to keep things simple.
      await actions.uploadSubmittalLog(files[0]);

      await actions.getSubmittals(SubmittalListTypes.ALL);
      await actions.querySubmittals();
    } catch (err) {
      console.log('unable to upload Submittal Log: ', err);
      toast.error(
        translate('toastText:submittalLogUploadError.message'),
        translate('toastText:submittalLogUploadError.title')
      );
    } finally {
      setUploadingSubmittalLog(false);
    }
  };

  const handleCheckAllClick = (): void => {
    const { appState, actions } = props;
    const { submittals, selectedSubmittals } = appState;

    if (
      selectedSubmittals.size > 0 &&
      appState.interactions.some((i) => i.type === 'UpdateMultipleSubmittals')
    ) {
      actions.clearInteractions();
    }
    const updatedSelectedSubmittals =
      selectedSubmittals.size > 0
        ? selectedSubmittals.clear()
        : submittals.keySeq().toSet();

    actions.setSelectedSubmittals(updatedSelectedSubmittals);
  };

  const handleRowClick = async (submittal: Submittal): Promise<void> => {
    const { appState, actions, history, location } = props;
    const { selectedSubmittals } = appState;
    const nextLocation = {
      ...location,
      search: `submittal=${submittal.id}`,
    };
    const viewing = appState.interactions.some(
      (i) => i.type === 'ViewSubmittal'
    );
    const singleEditing = appState.interactions.some(
      (i) => i.type === 'EditSubmittal'
    );
    const multiEditing = appState.interactions.some(
      (i) => (i as UpdateMultipleSubmittals) != null
    );

    // If editing another submittal with unsaved changes, warn user before switching.
    if (appState.unsavedChanges && singleEditing) {
      const modalState = new ModalState({
        id: 'ConfirmDiscardSubmittalChanges',
        portal: 'main',
        open: true,
        component: ConfirmModal,
        props: {
          acceptButtonText: translate('buttons:continue'),
          acceptButtonType: 'primary',
          denyButtonText: translate('buttons:cancel'),
          headerText: translate('submittalsPage:submittalsAreYouSure'),
          content: translate('submittalsPage:unsavedChanges'),
          acceptCallback: async (): Promise<void> => {
            const interaction = new ViewSubmittal({
              submittal,
            });

            await actions.clearInteractions();
            await actions.createInteraction(interaction);
            history.replace(nextLocation);
            actions.clearAllModals();
          },
          denyCallback: (): void => {
            actions.clearAllModals();
          },
        },
      });
      actions.createModal(modalState);
      return;
    }
    if (
      (multiEditing && appState.unsavedChanges) ||
      (!multiEditing && selectedSubmittals.size > 1)
    ) {
      const modalState = new ModalState({
        id: 'ConfirmUncheckSubmittals',
        portal: 'main',
        open: true,
        component: ConfirmModal,
        props: {
          acceptButtonText: translate('buttons:continue'),
          acceptButtonType: 'primary',
          denyButtonText: translate('buttons:cancel'),
          headerText: translate('submittalsPage:submittalsAreYouSure'),
          content: translate('submittalsPage:checkedItems'),
          acceptCallback: async (): Promise<void> => {
            await actions.clearInteractions();
            await actions.setSelectedSubmittals(Immutable.Set<string>());
            const interaction = new ViewSubmittal({
              submittal,
            });

            await actions.createInteraction(interaction);
            history.replace(nextLocation);

            actions.clearAllModals();
          },
          denyCallback: (): void => {
            actions.clearAllModals();
          },
        },
      });
      actions.createModal(modalState);
      return;
    }
    if (viewing) {
      const interaction = new ViewSubmittal({
        submittal,
      });

      await actions.clearInteractions();
      await actions.createInteraction(interaction);
      history.replace(nextLocation);
      actions.clearAllModals();
      return;
    }

    // If no submittal is being viewed, open a new interaction.
    await actions.clearInteractions();
    await actions.setSelectedSubmittals(Immutable.Set<string>());
    const interaction = new ViewSubmittal({
      submittal,
    });
    await actions.createInteraction(interaction);
    history.replace(nextLocation);
  };

  const handleCheckRow = (submittal: Submittal): void => {
    const { appState, actions } = props;
    const { selectedSubmittals } = appState;
    const multiEditing = appState.interactions.some(
      (i) => (i as UpdateMultipleSubmittals) != null
    );
    const singleEditing = appState.interactions.some(
      (i) => i.type === 'EditSubmittal'
    );

    if (
      selectedSubmittals.size > 0 &&
      singleEditing &&
      appState.unsavedChanges
    ) {
      const modalState = new ModalState({
        id: 'ConfirmDiscardPunchChanges',
        portal: 'main',
        open: true,
        component: ConfirmModal,
        props: {
          acceptButtonText: translate('buttons:continue'),
          acceptButtonType: 'primary',
          denyButtonText: translate('buttons:cancel'),
          headerText: translate('buttons:submittalsAreYouSure'),
          content: translate('buttons:unsavedChanges'),
          acceptCallback: async (): Promise<void> => {
            const updatedSelectedSubmittals = selectedSubmittals.has(
              submittal.id
            )
              ? selectedSubmittals.delete(submittal.id)
              : selectedSubmittals.add(submittal.id);

            actions.setSelectedSubmittals(updatedSelectedSubmittals);
            actions.clearInteractions();
            actions.clearAllModals();
          },
          denyCallback: (): void => {
            actions.clearAllModals();
          },
        },
      });
      actions.createModal(modalState);
      return;
    }
    if (selectedSubmittals.size > 0 && multiEditing) {
      actions.clearInteractions();
    }

    const updatedSelectedSubmittals = selectedSubmittals.has(submittal.id)
      ? selectedSubmittals.delete(submittal.id)
      : selectedSubmittals.add(submittal.id);

    actions.setSelectedSubmittals(updatedSelectedSubmittals);
  };

  const getOpenSubmittal = (): string | null => {
    const { appState } = props;
    const { interactions } = appState;

    const viewSubInteraction: ViewSubmittal = interactions.find(
      (i) => i.type === 'ViewSubmittal'
    ) as ViewSubmittal;

    if (viewSubInteraction) {
      return viewSubInteraction.submittal.id;
    }

    const editSubInteraction: EditSubmittal = interactions.find(
      (i) => i.type === 'EditSubmittal'
    ) as EditSubmittal;

    if (editSubInteraction) {
      return editSubInteraction.submittal.id;
    }

    return null;
  };

  const isMultiEditing = (): boolean => {
    const { interactions } = props.appState;
    return (interactions.first<null>() as UpdateMultipleSubmittals) != null;
  };

  const displaySubheader = (): boolean => {
    const { selectedSubmittals } = props.appState;
    const editing = isMultiEditing();
    const itemSelected = selectedSubmittals.size > 0;
    return itemSelected && !editing;
  };

  const { submittals, selectedSubmittals, queriedSubmittals } = props.appState;

  const queryParams = new URLSearchParams(props.location.search);
  const submittalQueryParam = queryParams.get('submittal');
  const qSubmittals = queriedSubmittals
    .map((id: string): Submittal | undefined => submittals.get(id))
    .filter(
      (s: Submittal | undefined): boolean => s !== undefined
    ) as Immutable.List<Submittal>;

  const openSubmittal = getOpenSubmittal() || '';
  const itemHeight = 64;

  return (
    <>
      {submittalQueryParam ? (
        <SubmittalDeepLink
          key={submittalQueryParam}
          submittalId={submittalQueryParam}
        />
      ) : null}
      <div className="column grow-1">
        {loadingSubmittals ? (
          <LoadingAnimation
            pageName={translate('submittalsPage:loadingSubmittals')}
          />
        ) : (
          <>
            <SubmittalLogTabSubHeader
              onAddSubmittal={handleAddSubmittal}
              onUploadSubmittalLog={handleUploadSubmittalLog}
              uploadingSubmittalLog={uploadingSubmittalLog}
            />
            <div
              className="mx4 mb4"
              style={{ height: `calc(100vh - 226px - ${bannerHeight}px)` }}
            >
              <StdList
                id="submittals-log-tab-list"
                gridTemplateColumns={gridTemplateColumns}
              >
                {displaySubheader() ? (
                  <SubmittalsSubHeader queriedSubmittals={qSubmittals} />
                ) : null}
                <StdListHeader>
                  <StdListHeaderCheckbox
                    id="subs-log-header-select-all-checkbox"
                    indeterminate={
                      selectedSubmittals.size > 0 &&
                      selectedSubmittals.size < submittals.size
                    }
                    checked={selectedSubmittals.size === submittals.size}
                    onChange={handleCheckAllClick}
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:submittalNumber')}
                    field="submittalNumber"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:specNumber')}
                    field="specNumber"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:specSection')}
                    field="specSection"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:description')}
                    field="specDescription"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:category')}
                    field="category"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('statusLabels:status')}
                    field="submittalStatus"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:dueDate')}
                    field="dueDate"
                    sortCell
                  />
                </StdListHeader>
                {submittals.size === 0 ? (
                  <NoSubmittals
                    onAddSubmittal={handleAddSubmittal}
                    onUploadSubmittalLog={handleUploadSubmittalLog}
                  />
                ) : (
                  <StdListBody enableScrolling={false}>
                    <AutoSizer>
                      {({ height, width }: StyleProps) => (
                        <FixedSizeList
                          height={height}
                          width={width}
                          itemCount={qSubmittals.size}
                          itemSize={itemHeight}
                          itemData={qSubmittals}
                        >
                          {({ style, index, data }: RenderingProps) =>
                            SubmittalLogRenderer(
                              index,
                              style,
                              data,
                              selectedSubmittals,
                              openSubmittal,
                              handleRowClick,
                              handleCheckRow
                            )
                          }
                        </FixedSizeList>
                      )}
                    </AutoSizer>
                  </StdListBody>
                )}
              </StdList>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default withAppStore(SubmittalLogTab);
